import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import {
  PssAuthService, PssSession, PssPlayerDetails,
  PssFleetInfo, PssFleetMember, FleetDailySnapshot,
} from '../../../services/pss-auth.service';
import { FeatureFlagService, FEATURE_FLAGS } from '../../../services/feature-flag.service';
import { Router } from '@angular/router';

interface FleetSnapshot {
  timestamp: string;
  allianceId: string;
  allianceName: string;
  trophy: string;
  divisionDesignId: string;
  ranking: string;
  members: PssFleetMember[];
}

@Component({
  selector: 'app-fleet-tab',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './fleet-tab.component.html',
  styleUrls: ['./fleet-tab.component.css'],
})
export class FleetTabComponent implements OnInit, OnDestroy {
  @Input() session: PssSession | null = null;
  @Input() details: PssPlayerDetails | null = null;

  constructor(
    private auth: PssAuthService,
    private featureFlags: FeatureFlagService,
    private router: Router,
  ) {}

  // ── Fleet data ──
  fleetInfo: PssFleetInfo | null = null;
  fleetLoading = false;
  fleetError = '';
  fleetDailySnapshots: FleetDailySnapshot[] = [];

  // ── Sort & filter ──
  fleetSortBy: 'trophies' | 'stars' | 'name' | 'rank' | 'attacks' = 'rank';
  fleetSortAsc = true;
  fleetFilterName = '';
  fleetFilterMinTrophies = ''; fleetFilterMaxTrophies = '';
  fleetFilterMinStars    = ''; fleetFilterMaxStars    = '';
  fleetFilterMinAttacks  = ''; fleetFilterMaxAttacks  = '';

  // ── Sub-view / snapshots ──
  fleetSubView: 'current' | 'history' = 'current';
  fleetSnapshots: FleetSnapshot[] = [];
  selectedSnapshot: FleetSnapshot | null = null;

  // ── Candidates toggle ──
  showCandidates = true;

  // ── Member expansion ──
  expandedMembers = new Set<string>();

  // ── Member action state ──
  memberBusy    = new Map<string, boolean>();
  memberError   = new Map<string, string>();
  memberSuccess = new Map<string, string>();
  memberHighlight = new Map<string, 'promoted' | 'demoted'>();

  // ── Friendship state ──
  friendIds        = new Set<string>();
  pendingFriendIds = new Set<string>();

  // ── Private messaging ──
  messagingMemberId: string | null = null;
  privateMessageInput = '';
  privateMessageSending = false;
  privateMessageError = '';

  // ── Fleet search + favorites ──
  displayAllianceId = '';
  fleetSearchTerm = '';
  fleetSearchResults: { allianceId: string; allianceName: string; memberCount?: string; trophy?: string }[] = [];
  fleetSearchLoading = false;
  fleetSearchError = '';
  fleetSearchOpen = false;

  favoriteFleets: { allianceId: string; allianceName: string }[] = [];
  favoriteAlliances = new Set<string>();

  private static readonly FLEET_SEARCH_STORAGE_KEY = 'pss_fleet_search';

  // ── Announcement modal ──
  showAnnouncementModal = false;
  announcementInput = '';
  announcementPushNotification = false;
  announcementSending = false;
  announcementError = '';

  readonly TOURN_DAILY_LIMIT = 6;

  ngOnInit(): void {
    this.displayAllianceId = this.details?.allianceId || '';
    this.loadFavoriteFleets();
    this.loadSavedFleetSearch();
    if (this.details?.allianceId) {
      this.fleetSnapshots = this.loadFleetSnapshots(this.details.allianceId);
      this.addFavoriteFleet(this.details.allianceId, this.details.fleetName || '');
    }
    this.loadFleet();
    this.loadFriendIds();
  }

  ngOnDestroy(): void {}

  // ── Fleet search / favorites helpers ──────────────────────────────────────

  private static readonly FAVORITES_STORAGE_KEY = 'pss_fleet_favorites';

  private loadFavoriteFleets(): void {
    try {
      const raw = localStorage.getItem(FleetTabComponent.FAVORITES_STORAGE_KEY);
      const saved = raw ? JSON.parse(raw) : [];
      this.favoriteFleets = Array.isArray(saved) ? saved.filter((f: any) => f && f.allianceId) : [];
    } catch {
      this.favoriteFleets = [];
    }
    this.favoriteAlliances = new Set(this.favoriteFleets.map(f => f.allianceId));
  }

  private saveFavoriteFleets(): void {
    try {
      localStorage.setItem(FleetTabComponent.FAVORITES_STORAGE_KEY, JSON.stringify(this.favoriteFleets));
    } catch {}
  }

  private saveFleetSearchState(): void {
    try {
      localStorage.setItem(FleetTabComponent.FLEET_SEARCH_STORAGE_KEY, JSON.stringify({
        query: this.fleetSearchTerm,
        results: this.fleetSearchResults,
        timestamp: new Date().toISOString(),
      }));
    } catch {}
  }

  private loadSavedFleetSearch(): void {
    if (this.isLastWeekOfCurrentMonth()) {
      this.fleetSearchTerm = '';
      this.fleetSearchResults = [];
      return;
    }
    try {
      const raw = localStorage.getItem(FleetTabComponent.FLEET_SEARCH_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed && parsed.query && Array.isArray(parsed.results)) {
        this.fleetSearchTerm = parsed.query;
        this.fleetSearchResults = parsed.results;
      }
    } catch {
      this.fleetSearchTerm = '';
      this.fleetSearchResults = [];
    }
  }

  private isLastWeekOfCurrentMonth(): boolean {
    const now = new Date();
    const lastDay = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0));
    const utcToday = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    const threshold = new Date(lastDay);
    threshold.setUTCDate(lastDay.getUTCDate() - 6);
    return utcToday >= threshold;
  }

  isFavoriteFleet(allianceId?: string): boolean {
    return !!allianceId && this.favoriteAlliances.has(allianceId);
  }

  addFavoriteFleet(allianceId: string, allianceName: string): void {
    if (!allianceId) return;
    if (this.favoriteAlliances.has(allianceId)) return;
    const favorite = { allianceId, allianceName: allianceName || allianceId };
    this.favoriteFleets = [favorite, ...this.favoriteFleets];
    this.favoriteAlliances.add(allianceId);
    this.saveFavoriteFleets();
  }

  removeFavoriteFleet(allianceId: string): void {
    if (!this.favoriteAlliances.has(allianceId)) return;
    this.favoriteFleets = this.favoriteFleets.filter(f => f.allianceId !== allianceId);
    this.favoriteAlliances.delete(allianceId);
    this.saveFavoriteFleets();
  }

  toggleFavoriteFleet(allianceId: string, allianceName: string): void {
    if (!allianceId) return;
    if (this.isFavoriteFleet(allianceId)) {
      this.removeFavoriteFleet(allianceId);
    } else {
      this.addFavoriteFleet(allianceId, allianceName);
    }
  }

  get favoritesLabel(): string {
    return this.favoriteFleets.length ? `Favorites (${this.favoriteFleets.length})` : 'Favorites';
  }

  // ── Helpers ──────────────────────────────────────────────────────────────

  private reauthAndRun(action: () => void): void {
    this.auth.reauthenticate().subscribe({
      next: () => action(),
      error: () => this.router.navigate(['/']),
    });
  }

  // ── Fleet load ───────────────────────────────────────────────────────────

  loadFleet(allianceId?: string, isRetry = false): void {
    const targetAllianceId = allianceId || this.displayAllianceId || this.details?.allianceId || '';
    if (!this.session || !targetAllianceId) return;
    this.displayAllianceId = targetAllianceId;
    this.fleetLoading = true;
    this.fleetError = '';
    this.auth.getFleetInfo(
      this.session.productionServer,
      targetAllianceId,
      this.session.accessToken,
    ).subscribe({
      next: info => {
        this.fleetInfo = info;
        this.fleetLoading = false;
        this.saveFleetSnapshot(info);
        this.fleetSnapshots = this.loadFleetSnapshots(info.allianceId);
        this.auth.persistFleetSnapshot(info).subscribe();
        const range = this.getFleetSnapshotDateRange();
        this.auth.getFleetSnapshots(info.allianceId, range.startDate, range.endDate).subscribe(snaps => {
          this.fleetDailySnapshots = snaps;
        });
        if (info.allianceId) {
          this.addFavoriteFleet(info.allianceId, info.allianceName);
        }
      },
      error: () => {
        if (!isRetry) { this.reauthAndRun(() => this.loadFleet(allianceId, true)); return; }
        this.fleetError = 'Could not load fleet data.';
        this.fleetLoading = false;
      },
    });
  }

  toggleFleetSearch(): void {
    this.fleetSearchOpen = !this.fleetSearchOpen;
  }

  searchFleets(): void {
    this.fleetSearchOpen = true;
    this.fleetSearchError = '';
    this.fleetSearchResults = [];
    const query = (this.fleetSearchTerm || '').trim();
    if (!query) {
      this.fleetSearchError = 'Enter fleet name or alliance ID to search';
      return;
    }
    if (!this.session) {
      this.fleetSearchError = 'No session available';
      return;
    }
    this.fleetSearchLoading = true;
    this.auth.searchPssFleets(query).subscribe({
      next: results => {
        const seen = new Set<string>();
        this.fleetSearchResults = results.filter(r => {
          if (!r.allianceId) return false;
          if (seen.has(r.allianceId)) return false;
          seen.add(r.allianceId);
          return true;
        });
        if (!this.fleetSearchResults.length) this.fleetSearchError = 'No fleets found';
        else this.saveFleetSearchState();
        this.fleetSearchLoading = false;
      },
      error: () => {
        this.fleetSearchError = 'Search failed';
        this.fleetSearchLoading = false;
      }
    });
  }

  // ── Snapshots ──────────────────────────────────────────────────────────

  private snapshotKey(allianceId: string): string {
    return `pss_fleet_snapshots_${allianceId}`;
  }

  saveFleetSnapshot(info: PssFleetInfo): void {
    const snapshot: FleetSnapshot = {
      timestamp: new Date().toISOString(),
      allianceId: info.allianceId,
      allianceName: info.allianceName,
      trophy: info.trophy,
      divisionDesignId: info.divisionDesignId,
      ranking: info.ranking,
      members: info.members,
    };
    try {
      const key = this.snapshotKey(info.allianceId);
      const existing: FleetSnapshot[] = JSON.parse(localStorage.getItem(key) ?? '[]');
      const today = snapshot.timestamp.slice(0, 10);
      const filtered = existing.filter(s => s.timestamp.slice(0, 10) !== today);
      filtered.unshift(snapshot);
      localStorage.setItem(key, JSON.stringify(filtered.slice(0, 24)));
    } catch {}
  }

  loadFleetSnapshots(allianceId: string): FleetSnapshot[] {
    try {
      return JSON.parse(localStorage.getItem(this.snapshotKey(allianceId)) ?? '[]');
    } catch { return []; }
  }

  selectSnapshot(s: FleetSnapshot | null): void { this.selectedSnapshot = s; }

  snapshotLabel(s: FleetSnapshot): string {
    const d = new Date(s.timestamp);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  }

  // ── Computed stats ───────────────────────────────────────────────────────

  get fleetTotalStars(): string {
    const members = this.selectedSnapshot?.members ?? this.fleetInfo?.members ?? [];
    const total = members.reduce((sum, m) => sum + this.getMemberTournamentStars(m), 0);
    return total > 0 ? String(total) : '—';
  }

  get fleetPlacement(): { rank: string; division: string; live: boolean } | null {
    const src = this.selectedSnapshot ?? this.fleetInfo;
    if (!src) return null;
    const divId = src.divisionDesignId;
    const ranking = (src as any).ranking ?? '0';
    if (divId !== '0' && ranking !== '0') {
      return { rank: ranking, division: this.divisionLabel(divId), live: true };
    }
    const snap = this.fleetSnapshots.find(s => s.divisionDesignId !== '0' && s.ranking && s.ranking !== '0');
    if (snap) {
      return { rank: snap.ranking, division: this.divisionLabel(snap.divisionDesignId), live: false };
    }
    return null;
  }

  get fleetAttackStats(): { done: number; total: number } | null {
    const info = this.selectedSnapshot ?? this.fleetInfo;
    if (!info || info.divisionDesignId === '0') return null;
    const active = info.members.filter(m => m.allianceMembership !== 'Candidate');
    const done = active.filter(m => parseInt(m.allianceScore || '0', 10) > 0).length;
    return { done, total: active.length };
  }

  get fleetCandidates(): PssFleetMember[] {
    const src = this.selectedSnapshot?.members ?? this.fleetInfo?.members ?? [];
    return src.filter(m => m.allianceMembership === 'Candidate')
      .sort((a, b) => parseInt(b.trophies) - parseInt(a.trophies));
  }

  get sortedFleetMembers(): PssFleetMember[] {
    let src = (this.selectedSnapshot?.members ?? this.fleetInfo?.members ?? [])
      .filter(m => m.allianceMembership !== 'Candidate');

    const nameQ = this.fleetFilterName.trim().toLowerCase();
    const minT = parseInt(this.fleetFilterMinTrophies, 10);
    const maxT = parseInt(this.fleetFilterMaxTrophies, 10);
    const minS = parseInt(this.fleetFilterMinStars,    10);
    const maxS = parseInt(this.fleetFilterMaxStars,    10);
    const minA = parseInt(this.fleetFilterMinAttacks,  10);
    const maxA = parseInt(this.fleetFilterMaxAttacks,  10);
    if (nameQ) src = src.filter(m => (m.name || '').toLowerCase().includes(nameQ));
    if (!isNaN(minT)) src = src.filter(m => parseInt(m.trophies             || '0', 10) >= minT);
    if (!isNaN(maxT)) src = src.filter(m => parseInt(m.trophies             || '0', 10) <= maxT);
    if (!isNaN(minS)) src = src.filter(m => this.getMemberTournamentStars(m) >= minS);
    if (!isNaN(maxS)) src = src.filter(m => this.getMemberTournamentStars(m) <= maxS);
    if (!isNaN(minA)) src = src.filter(m => parseInt(m.tournamentBonusScore || '0', 10) >= minA);
    if (!isNaN(maxA)) src = src.filter(m => parseInt(m.tournamentBonusScore || '0', 10) <= maxA);

    const dir = this.fleetSortAsc ? 1 : -1;
    return [...src].sort((a, b) => {
      switch (this.fleetSortBy) {
        case 'stars':   return dir * (this.getMemberTournamentStars(a) - this.getMemberTournamentStars(b));
        case 'attacks': return dir * (parseInt(a.tournamentBonusScore || '0') - parseInt(b.tournamentBonusScore || '0'));
        case 'name':    return dir * a.name.localeCompare(b.name);
        case 'rank': {
          const rd = this.rankOrder(a.allianceMembership) - this.rankOrder(b.allianceMembership);
          if (rd !== 0) return dir * rd;
          return -(parseInt(a.trophies || '0') - parseInt(b.trophies || '0'));
        }
        default: return dir * (parseInt(a.trophies || '0') - parseInt(b.trophies || '0'));
      }
    });
  }

  setFleetSort(col: 'trophies' | 'stars' | 'name' | 'rank' | 'attacks'): void {
    if (this.fleetSortBy === col) { this.fleetSortAsc = !this.fleetSortAsc; }
    else { this.fleetSortBy = col; this.fleetSortAsc = col === 'name' || col === 'rank'; }
  }

  toggleMemberDetail(userId: string): void {
    if (this.expandedMembers.has(userId)) this.expandedMembers.delete(userId);
    else this.expandedMembers.add(userId);
  }

  // ── Label helpers ────────────────────────────────────────────────────────

  divisionLabel(id: string): string {
    const map: Record<string, string> = { '1': 'A', '2': 'B', '3': 'C', '4': 'D' };
    return map[id] ?? '—';
  }

  memberRankAbbr(membership: string): string {
    const map: Record<string, string> = {
      FleetAdmiral: 'FA', ViceAdmiral: 'VA', Commander: 'Cdr',
      Major: 'Maj', Lieutenant: 'Lt', Ensign: 'Ens', Member: 'Mbr', Candidate: 'App',
    };
    return map[membership] ?? membership.slice(0, 3);
  }

  private rankOrder(membership: string): number {
    const order: Record<string, number> = {
      FleetAdmiral: 0, ViceAdmiral: 1, Commander: 2, Major: 3,
      Lieutenant: 4, Ensign: 5, Member: 6,
    };
    return order[membership] ?? 7;
  }

  formatNumber(val: string): string {
    const n = parseInt(val, 10);
    if (isNaN(n)) return val;
    return n.toLocaleString();
  }

  formatDate(iso: string): string {
    if (!iso || iso.startsWith('2000-01-01') || iso.startsWith('1900-01-01')) return '—';
    const d = new Date(iso.includes('T') ? iso + (iso.endsWith('Z') ? '' : 'Z') : iso + 'T00:00:00Z');
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  }

  // ── Fleet officer guards ─────────────────────────────────────────────────

  get isFleetOfficer(): boolean {
    const m = this.details?.fleetMembership;
    return ['Fleet Admiral', 'Vice Admiral', 'Commander', 'Major', 'Lieutenant', 'Ensign'].includes(m ?? '');
  }

  get isViewingOwnFleet(): boolean {
    return !!this.details?.allianceId && !!this.fleetInfo?.allianceId && this.details.allianceId === this.fleetInfo.allianceId;
  }

  get fleetManagementEnabled(): boolean {
    return this.featureFlags.isEnabled(FEATURE_FLAGS.FLEET_MANAGEMENT);
  }

  get basicPlusEnabled(): boolean {
    return this.featureFlags.isBasicPlus();
  }

  get restrictedMode(): boolean {
    return this.featureFlags.isRestrictedMode();
  }

  get fleetManagementLocked(): boolean {
    return this.featureFlags.isLocked(FEATURE_FLAGS.FLEET_MANAGEMENT);
  }

  get fleetManagementDisabledTooltip(): string {
    return this.fleetManagementLocked ? 'Fleet management is locked: unlock via subscription' : '';
  }

  canManageMember(m: PssFleetMember): boolean {
    if (!this.isViewingOwnFleet || !this.isFleetOfficer) return false;
    if (m.userId === this.session?.userId) return false;
    const rank = (this.details?.fleetMembership ?? '').replace(/\s/g, '');
    const myRank = this.rankOrder(rank);
    const theirRank = this.rankOrder(m.allianceMembership);
    if (theirRank <= myRank) return false;
    if (this.details?.fleetMembership === 'Vice Admiral' && m.allianceMembership === 'FleetAdmiral') return false;
    return true;
  }

  canPerformFleetMgmtOnMember(m: PssFleetMember): boolean {
    return this.fleetManagementEnabled && this.canManageMember(m);
  }

  private readonly RANK_HIERARCHY = [
    'Candidate', 'Member', 'Ensign', 'Lieutenant', 'Major', 'Commander', 'ViceAdmiral', 'FleetAdmiral',
  ];

  nextRankUp(membership: string): string | null {
    const idx = this.RANK_HIERARCHY.indexOf(membership);
    if (idx < 0 || idx >= this.RANK_HIERARCHY.length - 1) return null;
    return this.RANK_HIERARCHY[idx + 1];
  }

  nextRankDown(membership: string): string | null {
    const idx = this.RANK_HIERARCHY.indexOf(membership);
    if (idx <= 2) return null;
    return this.RANK_HIERARCHY[idx - 1];
  }

  canPromoteMember(m: PssFleetMember): boolean {
    if (!this.canManageMember(m)) return false;
    const myRankName = (this.details?.fleetMembership ?? '').replace(/\s/g, '');
    const newRank = this.nextRankUp(m.allianceMembership);
    if (!newRank) return false;
    // Cannot promote to the same rank as the officer, or higher.
    const myRank = this.rankOrder(myRankName);
    const targetRank = this.rankOrder(newRank);
    return targetRank > myRank;
  }

  canDemoteMember(m: PssFleetMember): boolean {
    if (!this.canManageMember(m)) return false;
    const myRankName = (this.details?.fleetMembership ?? '').replace(/\s/g, '');
    const newRank = this.nextRankDown(m.allianceMembership);
    if (!newRank) return false;
    // Cannot demote to the officer rank or above.
    const myRank = this.rankOrder(myRankName);
    const targetRank = this.rankOrder(newRank);
    return targetRank > myRank;
  }

  promoteMember(m: PssFleetMember): void {
    if (!this.fleetManagementEnabled || !this.canManageMember(m)) return;
    const newRank = this.nextRankUp(m.allianceMembership);
    if (!newRank) return;
    if (newRank === 'ViceAdmiral' && !confirm(`Promote ${m.name} to Vice Admiral?`)) return;
    this.applyMembership(m, newRank);
  }

  demoteMember(m: PssFleetMember): void {
    if (!this.fleetManagementEnabled || !this.canManageMember(m)) return;
    const newRank = this.nextRankDown(m.allianceMembership);
    if (!newRank) return;
    this.applyMembership(m, newRank);
  }

  kickMember(m: PssFleetMember): void {
    if (!this.fleetManagementEnabled || !this.canManageMember(m)) return;
    if (!confirm(`Kick ${m.name} from the fleet?`)) return;
    this.applyMembership(m, 'None');
  }

  private applyMembership(m: PssFleetMember, membership: string): void {
    const oldIdx = this.RANK_HIERARCHY.indexOf(m.allianceMembership);
    const newIdx = this.RANK_HIERARCHY.indexOf(membership);
    const direction: 'promoted' | 'demoted' = newIdx > oldIdx ? 'promoted' : 'demoted';
    this.memberBusy.set(m.userId, true);
    this.memberError.delete(m.userId);
    this.memberSuccess.delete(m.userId);
    this.auth.setMembership(m.userId, membership).subscribe({
      next: () => {
        if (membership === 'None') {
          if (this.fleetInfo) {
            this.fleetInfo.members = this.fleetInfo.members.filter(x => x.userId !== m.userId);
          }
        } else {
          m.allianceMembership = membership;
          this.memberHighlight.set(m.userId, direction);
          setTimeout(() => this.memberHighlight.delete(m.userId), 4000);
          this.memberSuccess.set(m.userId, '✓');
          setTimeout(() => this.memberSuccess.delete(m.userId), 2000);
        }
        this.memberBusy.delete(m.userId);
      },
      error: err => {
        this.memberError.set(m.userId, err?.error?.error || 'Failed');
        this.memberBusy.delete(m.userId);
      },
    });
  }

  // ── Friends ──────────────────────────────────────────────────────────────

  loadFriendIds(isRetry = false): void {
    this.auth.listFriends().subscribe({
      next: r => {
        this.friendIds        = new Set(r.confirmed.map(f => f.userId));
        this.pendingFriendIds = new Set(r.pending.map(f => f.userId));
      },
      error: () => {
        if (!isRetry) this.reauthAndRun(() => this.loadFriendIds(true));
      },
    });
  }

  addFriendAction(m: PssFleetMember): void {
    if (!this.fleetManagementEnabled) return;
    this.memberBusy.set(m.userId, true);
    this.memberError.delete(m.userId);
    this.memberSuccess.delete(m.userId);
    this.auth.addFriend(m.userId).subscribe({
      next: () => {
        this.memberSuccess.set(m.userId, 'Friend request sent');
        this.pendingFriendIds.add(m.userId);
        setTimeout(() => this.memberSuccess.delete(m.userId), 3000);
        this.memberBusy.delete(m.userId);
      },
      error: err => {
        this.memberError.set(m.userId, err?.error?.error || 'Failed');
        this.memberBusy.delete(m.userId);
      },
    });
  }

  get messagingMember(): PssFleetMember | null {
    if (!this.messagingMemberId || !this.fleetInfo) return null;
    return this.fleetInfo.members.find(m => m.userId === this.messagingMemberId) ?? null;
  }

  openMessagePanel(m: PssFleetMember): void {
    if (this.restrictedMode || !this.fleetManagementEnabled) return;
    this.messagingMemberId = this.messagingMemberId === m.userId ? null : m.userId;
    this.privateMessageInput = '';
    this.privateMessageError = '';
  }

  sendPrivateMessageAction(m: PssFleetMember): void {
    if (this.restrictedMode || !this.fleetManagementEnabled) return;
    if (!this.privateMessageInput.trim() || this.privateMessageSending) return;
    this.privateMessageSending = true;
    this.privateMessageError = '';
    this.auth.sendPrivateMessage(m.userId, this.privateMessageInput.trim()).subscribe({
      next: () => {
        this.privateMessageInput = '';
        this.privateMessageSending = false;
        this.messagingMemberId = null;
        this.memberSuccess.set(m.userId, 'Message sent');
        setTimeout(() => this.memberSuccess.delete(m.userId), 3000);
      },
      error: err => {
        this.privateMessageError = err?.error?.error || 'Failed to send message';
        this.privateMessageSending = false;
      },
    });
  }

  // ── Announcement ─────────────────────────────────────────────────────────

  openAnnouncementModal(): void {
    if (!this.isViewingOwnFleet || (this.restrictedMode && !this.fleetManagementEnabled)) return;
    this.announcementInput = '';
    this.announcementError = '';
    this.announcementPushNotification = false;
    this.showAnnouncementModal = true;
  }

  sendAnnouncement(): void {
    if (!this.isViewingOwnFleet || (this.restrictedMode && !this.fleetManagementEnabled)) return;
    if (!this.announcementInput.trim() || this.announcementSending || !this.details?.allianceId) return;
    this.announcementSending = true;
    this.announcementError = '';
    this.auth.sendFleetAnnouncement(this.details.allianceId, this.announcementInput.trim()).subscribe({
      next: () => {
        this.announcementInput = '';
        this.announcementPushNotification = false;
        this.announcementSending = false;
        this.showAnnouncementModal = false;
      },
      error: err => {
        this.announcementError = err?.error?.error || 'Failed to send announcement';
        this.announcementSending = false;
      },
    });
  }

  // ── Tournament ───────────────────────────────────────────────────────────

  get inTournamentPeriod(): boolean {
    const now = new Date();
    const lastDay = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0)).getUTCDate();
    return now.getUTCDate() >= lastDay - 6;
  }

  private getFleetSnapshotDateRange(): { startDate: string; endDate: string } {
    const now = new Date();

    if (this.inTournamentPeriod) {
      const lastDay = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0));
      const start = new Date(lastDay);
      start.setUTCDate(lastDay.getUTCDate() - 6);
      return {
        startDate: start.toISOString().slice(0, 10),
        endDate: lastDay.toISOString().slice(0, 10),
      };
    }

    // Off-season: include a wider window to find previous tournament snapshots.
    // This helps when the current month has only blank/0 rows after reset.
    const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    const start = new Date(end);
    start.setUTCDate(end.getUTCDate() - 59); // last 60 days, including current month if any rows exist
    return {
      startDate: start.toISOString().slice(0, 10),
      endDate: end.toISOString().slice(0, 10),
    };
  }

  private getLatestSnapshotStarsForUser(userId: string): number {
    const daily = this.fleetDailySnapshots
      .filter(s => s.userId === userId)
      .sort((a, b) => a.snapshotDate.localeCompare(b.snapshotDate));

    // Find most recent nonzero value (handles skipped days and partial history gaps)
    for (let i = daily.length - 1; i >= 0; i--) {
      if (daily[i].tournamentStars > 0) {
        return daily[i].tournamentStars;
      }
    }

    for (const snapshot of this.fleetSnapshots) {
      const member = snapshot.members.find(m => m.userId === userId);
      if (!member) continue;
      const stars = parseInt(member.allianceScore || '0', 10);
      if (stars > 0) return stars;
    }

    return 0;
  }

  getMemberTournamentStars(member: PssFleetMember): number {
    const current = parseInt(member.allianceScore || '0', 10);

    // If current has value, show it; otherwise fall back to latest known snapshot value.
    if (current > 0) {
      return current;
    }

    const fallback = this.getLatestSnapshotStarsForUser(member.userId);
    if (fallback > 0) {
      return fallback;
    }

    // If no historical data available, keep zero as a placeholder.
    return current;
  }

  getMemberStarLabel(member: PssFleetMember): string {
    const stars = this.getMemberTournamentStars(member);
    return stars > 0 ? stars.toLocaleString() : '—';
  }

  memberStarLabel(member: PssFleetMember): string {
    return this.getMemberStarLabel(member);
  }

  tournAttemptClass(attacks: string): string {
    const n = parseInt(attacks || '0', 10);
    if (!this.inTournamentPeriod) return 'tourn-low';
    if (n === 0) return 'tourn-low';
    if (n >= this.TOURN_DAILY_LIMIT) return 'tourn-full';
    return 'tourn-mid';
  }

  tournAccumulatedAttacks(userId: string): number {
    return this.fleetDailySnapshots
      .filter(s => s.userId === userId)
      .reduce((sum, s) => sum + s.dailyAttacksDone, 0);
  }

  tournAccumulatedMax(userId: string): number {
    return this.fleetDailySnapshots.filter(s => s.userId === userId).length * this.TOURN_DAILY_LIMIT;
  }

  tournAccumulatedClass(userId: string): string {
    const max = this.tournAccumulatedMax(userId);
    if (!this.inTournamentPeriod || max === 0) return 'tourn-low';
    const done = this.tournAccumulatedAttacks(userId);
    if (done === 0) return 'tourn-low';
    if (done >= max) return 'tourn-full';
    return 'tourn-mid';
  }

  // ── Last login ───────────────────────────────────────────────────────────

  memberLastLoginDays(iso: string): number {
    if (!iso || iso.startsWith('2000-01-01') || iso.startsWith('1900-01-01')) return -1;
    const d = new Date(iso.includes('T') ? iso + (iso.endsWith('Z') ? '' : 'Z') : iso + 'T00:00:00Z');
    return Math.floor((Date.now() - d.getTime()) / 86400000);
  }

  memberLastLoginLabel(iso: string): string {
    const days = this.memberLastLoginDays(iso);
    if (days < 0) return '—';
    if (days === 0) return 'Today';
    if (days === 1) return '1d';
    return `${days}d`;
  }

  memberLastLoginClass(iso: string): string {
    const days = this.memberLastLoginDays(iso);
    if (days < 0 || days <= 1) return 'login-ok';
    if (days <= 3) return 'login-warn';
    return 'login-stale';
  }

  // ── CSV export ───────────────────────────────────────────────────────────

  exportFleetCsv(): void {
    const members = this.sortedFleetMembers;
    const header = ['Name', 'Rank', 'Trophies', 'Stars', 'Attacks', 'Donated', 'Last Login'];
    const rows = members.map(m => [
      m.name, m.allianceMembership, m.trophies, this.getMemberTournamentStars(m),
      m.tournamentBonusScore, m.crewDonated, this.memberLastLoginLabel(m.lastLoginDate),
    ]);
    const csv = [header, ...rows]
      .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fleet-${this.fleetInfo?.allianceName ?? 'export'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }
}
